"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";

import type { SmartPaginationFormat } from "@/components/ui/smartPagination";

const FORMAT_ORDER: SmartPaginationFormat[] = ["short", "medium", "large"];

function getFormatIndex(format: SmartPaginationFormat) {
  return FORMAT_ORDER.indexOf(format);
}

function getIntrinsicWidth(element: HTMLElement) {
  return Math.max(element.getBoundingClientRect().width, element.scrollWidth);
}

export function getMoreCompactPaginationFormat(
  format: SmartPaginationFormat
): SmartPaginationFormat {
  return FORMAT_ORDER[Math.max(0, getFormatIndex(format) - 1)];
}

export function getFormatWithinMaximum(
  format: SmartPaginationFormat,
  maximumFormat: SmartPaginationFormat
): SmartPaginationFormat {
  return FORMAT_ORDER[Math.min(getFormatIndex(format), getFormatIndex(maximumFormat))];
}

export function useFittedPaginationFormat(maximumFormat: SmartPaginationFormat) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [controls, setControls] = useState<HTMLDivElement | null>(null);
  const [pagination, setPagination] = useState<HTMLDivElement | null>(null);
  const previousContainerWidthRef = useRef<number | null>(null);
  const [measuredFormat, setMeasuredFormat] = useState<SmartPaginationFormat>(maximumFormat);
  const format = getFormatWithinMaximum(measuredFormat, maximumFormat);
  const containerRef = useCallback((node: HTMLDivElement | null) => setContainer(node), []);
  const controlsRef = useCallback((node: HTMLDivElement | null) => setControls(node), []);
  const paginationRef = useCallback((node: HTMLDivElement | null) => setPagination(node), []);

  const compactUntilContentFits = useCallback(() => {
    if (!container || !controls || !pagination) return;

    const containerWidth = container.getBoundingClientRect().width;
    const controlsWidth = getIntrinsicWidth(controls);
    const paginationWidth = getIntrinsicWidth(pagination);
    const gap = Number.parseFloat(window.getComputedStyle(container).columnGap) || 0;

    if (controlsWidth + paginationWidth + gap > containerWidth) {
      setMeasuredFormat(getMoreCompactPaginationFormat(format));
    }
  }, [container, controls, format, pagination]);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => setMeasuredFormat(maximumFormat));
    return () => window.cancelAnimationFrame(frame);
  }, [maximumFormat]);

  useLayoutEffect(() => {
    if (!container || !controls || !pagination) return;

    const updateFormat = () => {
      const containerWidth = container.getBoundingClientRect().width;
      const previousContainerWidth = previousContainerWidthRef.current;
      previousContainerWidthRef.current = containerWidth;

      if (previousContainerWidth !== null && containerWidth > previousContainerWidth) {
        setMeasuredFormat(maximumFormat);
        return;
      }

      compactUntilContentFits();
    };

    previousContainerWidthRef.current = container.getBoundingClientRect().width;
    const initialMeasurementFrame = window.requestAnimationFrame(updateFormat);

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateFormat);
      return () => {
        window.cancelAnimationFrame(initialMeasurementFrame);
        window.removeEventListener("resize", updateFormat);
      };
    }

    const observer = new ResizeObserver(updateFormat);
    observer.observe(container);
    observer.observe(controls);
    observer.observe(pagination);

    return () => {
      window.cancelAnimationFrame(initialMeasurementFrame);
      observer.disconnect();
    };
  }, [compactUntilContentFits, container, controls, maximumFormat, pagination]);

  return { containerRef, controlsRef, format, paginationRef };
}
